import { useCallback } from "react";
import { usePolicyEditStructureNodeManager } from "components/policyEdit/structure/PolicyEditStructureNodeManager";
import { useUpdatePolicyCriteriaMutation } from "services/policy";
import { environmentConfig } from "config/environmentConfig";

/**
 * Transform UI criteria to KQL format expected by the API
 * Combines all constraints into a single KQL query string following the format:
 * AgreementAudit | where Template.Id == '<templateId>' where <constraints> | summarize arg_max(_timestamp, *) by CustomerId
 * Note: StartEffectiveDate filter is preserved if it exists in originalQuery, otherwise it's added during publish
 * @param criteria - Array of UI criteria objects
 * @param templateId - The template ID to query
 * @param originalQuery - Optional: existing query to preserve StartEffectiveDate from
 * @returns KQL query string
 */
const transformCriteriaToKQL = (criteria: any[], templateId?: string, originalQuery?: string) => {
  // Strip 'template-' prefix if present (node IDs have prefix, but KQL query needs just the GUID)
  const cleanTemplateId = templateId?.replace(/^template-/, '') || '<templateId>';
  
  // Extract StartEffectiveDate from original query if it exists
  let existingStartEffectiveDate = '';
  if (originalQuery) {
    const dateMatch = originalQuery.match(/and\s+StartEffectiveDate\s*<\s*datetime\('([^']*)'\)/);
    if (dateMatch) {
      existingStartEffectiveDate = ` and StartEffectiveDate < datetime('${dateMatch[1]}')`;
    }
  }
  
  // Start with base query: AgreementAudit | where Template.Id == '<templateId>'
  let query = `AgreementAudit | where Template.Id == '${cleanTemplateId}'`;
  
  // Build constraint expressions
  if (criteria && criteria.length > 0) {
    const kqlExpressions = criteria.map((criterion: any) => {
      const source = criterion.key || criterion.name;
      const operator = criterion.operator || "Include";
      
      // Use values (actual names/IDs) instead of valueDisplays (human-readable)
      let actualValues: string[] = [];
      if (criterion.values && Array.isArray(criterion.values)) {
        actualValues = criterion.values;
      } else if (criterion.value) {
        actualValues = [criterion.value];
      }

      if (actualValues.length === 0) {
        return null;
      }

      // Format values for KQL query - wrap each value in single quotes
      const formattedValues = actualValues.map((v: string) => `'${v}'`).join(', ');
      
      // Use Constraints. prefix for constraint fields (e.g., "Constraints.Country")
      // Use "!in" for Exclude and "in" for Include (KQL syntax)
      const kqlOperator = operator === "Exclude" ? "!in" : "in";
      
      return `Constraints.${source} ${kqlOperator} (${formattedValues})`;
    }).filter(Boolean);

    // Add constraint expressions with 'and' separator
    if (kqlExpressions.length > 0) {
      query += ` and ${kqlExpressions.join(' and ')}`;
    }
  }
  
  // Add hardcoded Status filter
  query += ` and Status == 'Active'`;
  
  // Preserve StartEffectiveDate if it existed in the original query
  if (existingStartEffectiveDate) {
    query += existingStartEffectiveDate;
  }
  
  // Add summarize by CustomerId with arg_max on _timestamp
  query += ' | summarize arg_max(_timestamp, *) by CustomerId';
  
  // Return single criteria object with combined query
  return [{
    filterType: "KQL",
    source: "Agreement",
    query
  }];
};

/**
 * Hook for managing policy structure mutations
 * Similar to useTemplateStructureEditor but for policy criteria management
 */
export const usePolicyStructureEditor = (onClosePanel?: () => void) => {
  const { 
    addCriteriaToTemplate, 
    removeCriteriaFromTemplate, 
    updateTemplateCriteria: updateTemplateCriteriaLocal,
    policyId,
    policyInfo,
    onCriteriaApplied,
  } = usePolicyEditStructureNodeManager();
  
  const [updatePolicyCriteria] = useUpdatePolicyCriteriaMutation();

  /**
   * Update policy template constraints/criteria
   * @param templateId - The template ID to update
   * @param criteria - Array of criteria to set for the template
   */
  const updateTemplateCriteria = useCallback(
    async (templateId: string, criteria: any[]) => {
      try {
        // Update local state immediately for UI feedback
        if (updateTemplateCriteriaLocal) {
          updateTemplateCriteriaLocal(templateId, criteria);
        }
        
        // Transform criteria to KQL format for API
        // Pass existing query to preserve StartEffectiveDate if present
        const existingQuery = policyInfo?.criteria?.[0]?.query;
        const kqlCriteria = criteria.length > 0 ? transformCriteriaToKQL(criteria, templateId, existingQuery) : [];
        
        // Call API to update policy with criteria
        if (policyId && policyInfo) {
          await updatePolicyCriteria({
            id: policyId,
            templateId: policyInfo.templateId,
            templateName: policyInfo.template,
            criteria: kqlCriteria,
            cloud: environmentConfig.cloud,
            environment: environmentConfig.environment,
            policyType: policyInfo.type,
          }).unwrap();
          
          // Call the refetch callback to get latest data from API
          if (onCriteriaApplied) {
            onCriteriaApplied();
          }
        }
        
        if (onClosePanel) {
          onClosePanel();
        }
        
        return { success: true };
      } catch (error) {
        return { 
          success: false, 
          error: "Failed to save criteria. Please try again." 
        };
      }
    },
    [onClosePanel, updateTemplateCriteriaLocal, updatePolicyCriteria, policyId, policyInfo, onCriteriaApplied]
  );

  /**
   * Add a single criterion to a template
   * @param templateId - The template ID
   * @param criterion - The criterion to add
   */
  const addCriterionToTemplate = useCallback(
    (templateId: string, criterion: any) => {
      if (addCriteriaToTemplate) {
        addCriteriaToTemplate(templateId, criterion);
      }
    },
    [addCriteriaToTemplate]
  );

  /**
   * Remove a single criterion from a template
   * @param templateId - The template ID
   * @param criterionId - The criterion ID to remove
   */
  const removeCriterionFromTemplate = useCallback(
    (templateId: string, criterionId: string) => {
      if (removeCriteriaFromTemplate) {
        removeCriteriaFromTemplate(templateId, criterionId);
      }
    },
    [removeCriteriaFromTemplate]
  );

  return {
    updateTemplateCriteria,
    addCriterionToTemplate,
    removeCriterionFromTemplate,
  };
};

export default usePolicyStructureEditor;